// DefectSignature (FNP-904) — the TRIAGE view of what the synthetic-user fleet
// found. One row per DEFECT, not per observation: the dedup engine (FNP-903)
// folds ~60 failing observations into ~17 signatures, which is the difference
// between a table a human can triage and one they read line by line.
//
// Mirrors the testing-api schema exactly:
//   type DefectSignature {
//     id: ID!  actionCode: String!  sampleError: String  normalizedError: String
//     outcome: ObservationOutcome!  verificationResult: VerificationResult
//     firstSeenAt: AWSDateTime!  lastSeenAt: AWSDateTime!
//     occurrenceCount: Int!  distinctUserCount: Int!
//     status: DefectStatus!  jiraKey: String  resolvedAt: AWSDateTime
//     recordType: String!  created: AWSDateTime  updated: AWSDateTime
//   }
//
// ⚠️ `id` IS THE FINGERPRINT — a sha256 of actionCode + the normalized error,
// not a uuid. Two things follow for this module: it is stable across wakes (so a
// deep link to a defect keeps working), and it is opaque (so it is rendered
// truncated and monospaced, never as a meaningful identifier).

import {
    ObservationOutcome,
    VerificationResult,
} from '../test-observations/types';

// Re-exported so this module's components can speak in one vocabulary without
// importing from a sibling feature everywhere. The enums are genuinely shared —
// both entities carry the same outcome/verification semantics.
export {
    ObservationOutcome,
    VerificationResult,
};

export enum DefectStatus {
    // Seen by the sweep, not yet triaged by a human.
    NEW = 'NEW',
    // A human looked and decided it is real, but not yet worth a ticket.
    ACKNOWLEDGED = 'ACKNOWLEDGED',
    // A Jira issue exists — jiraKey is set.
    FILED = 'FILED',
    // Has not recurred for the resolution window. Set by the SWEEP, never by a
    // human — it is the system's answer to "is it fixed?".
    RESOLVED = 'RESOLVED',
}

export type DefectSignature = {
    // The fingerprint. Opaque and deterministic — see the header.
    id: string,
    // A HANDLE, not a script (the Action seam) — same rule as TestObservation.
    actionCode: string,
    // One real error string from the group, kept verbatim for a human to read.
    sampleError?: string | null,
    // The normalized form the fingerprint was computed from, with volatile parts
    // replaced by <uuid>/<email>/<epoch>/<ms>. DISPLAY-ONLY: it exists so "why
    // are these the same defect?" is answerable without reading the source, and
    // must never be parsed back.
    normalizedError?: string | null,
    outcome: ObservationOutcome,
    verificationResult?: VerificationResult | null,
    firstSeenAt: string,
    lastSeenAt: string,
    occurrenceCount: number,
    // How many DISTINCT synthetic users hit it. A defect one user hits twenty
    // times reads very differently from one twenty users hit once — that is the
    // whole reason this field exists (and why the engine stores a set, not a
    // counter).
    distinctUserCount: number,
    status: DefectStatus,
    jiraKey?: string | null,
    // Set when the sweep concluded the defect stopped recurring. A RESOLVED row
    // whose lastSeenAt is LATER than this is a REGRESSION — the defect came back.
    resolvedAt?: string | null,
    created?: string | null,
    updated?: string | null,
};

// Server-side filter. Mirrors DefectSignaturesFilterInput exactly — only these
// three fields exist on the input, so anything else must be a client-side
// narrowing over loaded rows.
export type DefectSignaturesFilter = {
    actionCode?: string,
    status?: string,
    verificationResult?: string,
};

export type ListDefectSignatures = {
    listDefectSignatures: { items: DefectSignature[], nextToken?: string }
}

export type GetDefectSignature = {
    getDefectSignature: DefectSignature
}

export type UpdateDefectSignature = {
    updateDefectSignature: DefectSignature
}

// deleteDefectSignature returns the deleted id (`ID`), not the row.
export type DeleteDefectSignature = {
    deleteDefectSignature: string
}
